import React, { createContext, ReactNode, useContext, useState } from 'react';
import { AccordionHeader } from './AccordionHeader';
import { AccordionPanel } from './AccordionPanel';
import clsx from 'clsx';

export interface AccordionHeaderAction {
  name: string;
  action(): void;
  expandOnClick?: boolean;
}

interface AccordionProps extends BaseProps {
  children: ReactNode;
  defaultExpanded?: boolean;
  showFocus?: boolean;
}

type AccordionContext = {
  expanded: boolean;
  handleExpand(expanded: boolean): void;
};

const AccordionContext = createContext<AccordionContext | undefined>(undefined);

const Accordion = ({
  children,
  className,
  defaultExpanded = false,
  showFocus = false,
  ...rest
}: AccordionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <AccordionContext.Provider value={{ expanded, handleExpand: setExpanded }}>
      <details
        className={clsx(className, {
          'ring-1 ring-primary': showFocus && expanded,
        })}
        open={expanded}
        {...rest}
      >
        {children}
      </details>
    </AccordionContext.Provider>
  );
};

const useAccordion = () => {
  const context = useContext(AccordionContext);

  if (context === undefined)
    throw new Error('useAccordion must be used within an AccordionProvider');

  return context;
};

export { Accordion, AccordionHeader, AccordionPanel, useAccordion };
